import apiClient from "../api";

export const generateReport = async (endpoint, data, responseType = 'blob') => {
  try {
    const response = await apiClient.post(endpoint, data, {
      responseType,
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  } catch (error) {
    throw await parseBlobError(error);
  }
};

// ─── Helper central : lit le vrai message même si la réponse est un Blob ─────
export const parseBlobError = async (error) => {
  if (error.response?.data instanceof Blob) {
    try {
      const text = await error.response.data.text();
      try {
        const json = JSON.parse(text);
        const msg = json.message || json.Message || json.detail || json.title;
        if (msg) return new Error(msg);
      } catch {
        if (text?.trim()) return new Error(text.trim());
      }
    } catch {
      // Impossible de lire le blob
    }
  }

  const status = error.response?.status;
  if (status === 400) return new Error(error.response?.data?.message || 'Paramètres invalides pour la génération du rapport');
  if (status === 404) return new Error('Aucune donnée trouvée pour générer le rapport');
  if (status === 500) return new Error(error.response?.data?.message || 'Erreur interne du serveur lors de la génération du rapport');

  return new Error(error.message || 'Erreur lors de la génération du rapport');
};

// ─── Téléchargement ────────────────────────────────────────────────────────────
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const ReportService = {
  // 1. Liste des actionnaires (PDF)
  generateShareholdersList: async (domainId, securityId, includeZeroBalance = false) =>
    generateReport('/api/reports/shareholders/list', {
      DomainId: domainId,
      SecurityId: securityId,
      IncludeZeroBalance: includeZeroBalance,
      ReportDate: new Date().toISOString()
    }, 'blob'),

  // 2. Plan comptable (PDF)
  generateAccountingPlan: async (domainId, securityId) =>
    generateReport('/api/reports/accounting-plan', {
      DomainId: domainId,
      SecurityId: securityId,
      ReportDate: new Date().toISOString()
    }, 'blob'),

  // 3. Historique des comptes (ZIP)
  generateAccountHistory: async (domainId, securityId, startDate, endDate) =>
    generateReport('/api/reports/account-history-by-client', {
      DomainId: domainId,
      SecurityId: securityId,
      StartDate: startDate || null,
      EndDate: endDate || null
    }, 'blob'),

  // 4. Journal auxiliaire d'émission (PDF)
  generateEmissionJournal: async (domainId, securityId, startDate, endDate) =>
    generateReport('/api/reports/emission-journal', {
      DomainId: domainId,
      SecurityId: securityId,
      StartDate: startDate || null,
      EndDate: endDate || null
    }, 'blob'),

  // ── Marché privé — FormData ──────────────────────────────────────────────────

  // 5. Attestation de propriété (ZIP)
  generateAttestationPropriete: async (domainId, securityId) => {
    const fd = new FormData();
    fd.append('DomainId', domainId.toString());
    fd.append('SecurityId', securityId.toString());
    try {
      const res = await apiClient.post('/api/MarchePrivee/attestationPropriete-pdf-zip', fd, {
        responseType: 'blob', headers: { 'Content-Type': undefined }
      });
      return res.data;
    } catch (err) { throw await parseBlobError(err); }
  },

  // 6. Compte conservation (ZIP)
  generateCompteConservation: async (domainId, securityId) => {
    const fd = new FormData();
    fd.append('DomainId', domainId.toString());
    fd.append('SecurityId', securityId.toString());
    try {
      const res = await apiClient.post('/api/MarchePrivee/Compte-conservation-valeur-mobiliere-pdf-zip', fd, {
        responseType: 'blob', headers: { 'Content-Type': undefined }
      });
      return res.data;
    } catch (err) { throw await parseBlobError(err); }
  },

  // 7. Registre de conservation (ZIP)
  generateRegistreConservation: async (domainId, securityId) => {
    const fd = new FormData();
    fd.append('DomainId', domainId.toString());
    fd.append('SecurityId', securityId.toString());
    try {
      const res = await apiClient.post('/api/MarchePrivee/Registre-conservation-pdf-zip', fd, {
        responseType: 'blob', headers: { 'Content-Type': undefined }
      });
      return res.data;
    } catch (err) { throw await parseBlobError(err); }
  },

  // 8. Opération comptabilité (PDF) — query params, corps vide
  generateOperationComptabilite: async (domainId, securityId, periode = 3) => {
    const url = `/api/MarchePrivee/Operation-Comptabilite-pdf?securityId=${securityId}&domainId=${domainId}&periode=${periode}`;
    try {
      const res = await apiClient.post(url, null, {
        responseType: 'blob',
        headers: { Accept: '*/*' }
      });
      return res.data;
    } catch (err) { throw await parseBlobError(err); }
  },

  // 9. Compte global d'émission (PDF)
  generateCompteGlobal: async (domainId, securityId) => {
    const fd = new FormData();
    fd.append('DomainId', domainId.toString());
    fd.append('SecurityId', securityId.toString());
    try {
      const res = await apiClient.post('/api/MarchePrivee/Compte-global-pdf', fd, {
        responseType: 'blob', headers: { 'Content-Type': undefined }
      });
      return res.data;
    } catch (err) { throw await parseBlobError(err); }
  },

  // ── Nouveaux rapports ────────────────────────────────────────────────────────

  // 10. Historique Valeur Nominale (PDF)
  generateHistoriqueValeurNominal: async (domainId, securityId) => {
    const fd = new FormData();
    fd.append('DomainId', domainId.toString());
    fd.append('SecurityId', securityId.toString());
    try {
      const res = await apiClient.post('/api/MarchePrivee/historique-valeur-nominal-pdf', fd, {
        responseType: 'blob', headers: { 'Content-Type': undefined }
      });
      return res.data;
    } catch (err) { throw await parseBlobError(err); }
  },

  // 11. Restriction Titres (ZIP)
  generateRestrictionTitres: async (domainId, securityId) => {
    const fd = new FormData();
    fd.append('DomainId', domainId.toString());
    fd.append('SecurityId', securityId.toString());
    try {
      const res = await apiClient.post('/api/MarchePrivee/restriction-titres-pdf-zip', fd, {
        responseType: 'blob', headers: { 'Content-Type': undefined }
      });
      return res.data;
    } catch (err) { throw await parseBlobError(err); }
  },

  // 12. Tableau de Bord Action (ZIP)
  generateTableauBordAction: async (domainId, securityId) => {
    const fd = new FormData();
    fd.append('DomainId', domainId.toString());
    fd.append('SecurityId', securityId.toString());
    try {
      const res = await apiClient.post('/api/MarchePrivee/tableau-bord-action-pdf-zip', fd, {
        responseType: 'blob', headers: { 'Content-Type': undefined }
      });
      return res.data;
    } catch (err) { throw await parseBlobError(err); }
  }
};

export default ReportService;